import { keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { useEffect } from "react";

import { AssetLabel } from "@/components/asset-label";
import { type Column, DataList } from "@/components/data-list";
import { Pager, pageCount } from "@/components/pager";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import type { OpportunityView } from "@/generated/api/model";
import { useGetOpportunities } from "@/generated/api/opportunities/opportunities";
import { tokenIdOfPair } from "@/lib/cardanoscan";

import { ada, chainTime, downloadCsv, duration, route, toCsv } from "./format";
import type { LogFilter } from "./search";
import { StatusBadges } from "./StatusBadges";

const PAGE_SIZE = 50;

export interface LogView {
    filter: LogFilter;
    page: number;
}

/** One page of recorded windows, newest first; given `pair`, only that pair's. The URL owns `view`. */
export function OpportunityLog({
    pair,
    view,
    onView,
}: {
    pair?: string;
    view: LogView;
    onView: (view: LogView, replace?: boolean) => void;
}) {
    const { filter, page } = view;
    const log = useGetOpportunities(
        { actionable_only: filter === "actionable", limit: PAGE_SIZE, page, pair },
        { query: { placeholderData: keepPreviousData } },
    );
    const pages = log.data ? pageCount(log.data.total, PAGE_SIZE) : undefined;

    // A link to a page the log has since outgrown (or a typed one) lands on the last page instead.
    useEffect(() => {
        if (pages !== undefined && page > pages) onView({ filter, page: pages }, true);
    }, [filter, onView, page, pages]);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1.5" role="group" aria-label="Filter">
                    {(["actionable", "all"] as const).map((option) => (
                        <Button
                            key={option}
                            size="sm"
                            variant={option === filter ? "default" : "outline"}
                            aria-pressed={option === filter}
                            onClick={() => onView({ filter: option, page: 1 })}
                        >
                            {option === "actionable" ? "Actionable" : "All"}
                        </Button>
                    ))}
                </div>
                {log.data && log.data.windows.length > 0 && (
                    <Button size="sm" variant="outline" onClick={() => downloadLog(log.data.windows, page)}>
                        <Download />
                        CSV
                    </Button>
                )}
            </div>
            <QueryState
                query={log}
                isEmpty={(data) => data.total === 0}
                emptyMessage={
                    filter === "actionable"
                        ? "No actionable opportunity recorded yet. Show all to see the shorter or smaller ones."
                        : "No opportunity recorded yet."
                }
            >
                {(data) => (
                    <>
                        <DataList
                            rows={data.windows}
                            columns={COLUMNS}
                            rowKey={(row) => String(row.window.id)}
                            label="Opportunities"
                        />
                        <Pager
                            page={page}
                            total={data.total}
                            pageSize={PAGE_SIZE}
                            noun="windows"
                            onPage={(to) => onView({ filter, page: to })}
                        />
                    </>
                )}
            </QueryState>
        </div>
    );
}

const COLUMNS: Column<OpportunityView>[] = [
    {
        label: "Token",
        title: true,
        render: (row) => (
            <AssetLabel id={tokenIdOfPair(row.window.pair)} name={row.window.token_name}>
                <Link
                    to="/stats/$id"
                    params={{ id: String(row.window.id) }}
                    className="font-semibold underline-offset-4 hover:underline"
                >
                    {row.window.token_name}
                </Link>
            </AssetLabel>
        ),
    },
    { label: "Peak", title: true, value: (row) => `${ada(row.window.peak_profit_lovelace)} ADA` },
    { label: "Opened", value: (row) => chainTime(row.window.opened_at), wide: true },
    { label: "Open for", value: (row) => duration(row.duration_secs) },
    { label: "Route", value: (row) => route(row.window.peak) },
    { label: "Events", value: (row) => row.window.recorded_events + row.window.dropped_events },
    { label: "Status", render: (row) => <StatusBadges view={row} />, wide: true },
];

function downloadLog(rows: OpportunityView[], page: number) {
    const csv = toCsv(
        ["id", "token", "pair", "status", "actionable", "opened_utc", "open_secs", "peak_ada", "route", "events"],
        rows.map(({ window, duration_secs, actionable }) => [
            window.id,
            window.token_name,
            window.pair,
            window.status,
            actionable ? "yes" : "no",
            chainTime(window.opened_at),
            duration_secs,
            ada(window.peak_profit_lovelace),
            route(window.peak),
            window.recorded_events + window.dropped_events,
        ]),
    );
    downloadCsv(`arbitrage-opportunities-page-${page}.csv`, csv);
}
